import { DateField } from "./DateField";
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import paymentService, { PaymentResponse, PaymentSummary } from '../services/paymentService';
import { getReportMonth, getReportMonthRange, getReportToday, getReportWeekRange } from '../utils/reportDate';

interface PaymentDashboardSummary {
  totalAmount: number;
  totalCount: number;
  cashAmount: number;
  cashCount: number;
  cardAmount: number;
  cardCount: number;
  fundTransferAmount: number;
  fundTransferCount: number;
  payments: PaymentResponse[];
}

interface MonthlyMemberPayment {
  memberId: number;
  memberName: string;
  memberNic: string;
  membershipType: string;
  peakHours: string;
  startDate: string;
  memberStatus: string;
  month: string; // YYYY-MM format
  payments: {
    paymentId: number;
    amount: number;
    paymentMethod: string;
    paymentDate: string;
    transactionId: string;
    notes?: string;
  }[];
  totalAmount: number;
}

interface AllTimePaymentData {
  months: string[]; // Array of months in YYYY-MM format
  memberPayments: MonthlyMemberPayment[];
}

const PaymentsDashboard: React.FC = () => {
  const allTimeTableRef = useRef<HTMLDivElement>(null);
  const [tab, setTab] = useState<'daily' | 'weekly' | 'monthly' | 'alltime'>('daily');
  const [selectedDate, setSelectedDate] = useState<string>(getReportToday);
  const [weekRange, setWeekRange] = useState<{start: string, end: string}>(() => getReportWeekRange());
  const [selectedMonth, setSelectedMonth] = useState<string>(getReportMonth);
  const [summary, setSummary] = useState<PaymentDashboardSummary | null>(null);
  const [allTimeData, setAllTimeData] = useState<AllTimePaymentData | null>(null);
  const [memberFilter, setMemberFilter] = useState<string>('');
  const [excludeInactive, setExcludeInactive] = useState<boolean>(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('Effect triggered with:', { tab, selectedDate, weekRange, selectedMonth });
    fetchPayments();
    // eslint-disable-next-line
  }, [tab, selectedDate, weekRange.start, weekRange.end, selectedMonth]);

  const fetchPayments = async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 'alltime') {
        await fetchAllTimeData();
      } else {
        let startDate: string;
        let endDate: string;
        
        if (tab === 'daily') {
          startDate = selectedDate;
          endDate = selectedDate;
        } else if (tab === 'weekly') {
          startDate = weekRange.start;
          endDate = weekRange.end;
        } else { // monthly
          const monthRange = getReportMonthRange(selectedMonth);
          startDate = monthRange.start;
          endDate = monthRange.end;
        }

        console.log('Fetching payments for:', { startDate, endDate });

        // Fetch payment summaries and detailed payments
        const [summariesResponse, paymentsResponse] = await Promise.all([
          paymentService.getPaymentSummaries(startDate, endDate),
          paymentService.getPaymentsByDateRange(startDate, endDate, 0, 100)
        ]);

        // Aggregate summaries
        const aggregatedSummary: PaymentDashboardSummary = {
          totalAmount: 0,
          totalCount: 0,
          cashAmount: 0,
          cashCount: 0,
          cardAmount: 0,
          cardCount: 0,
          fundTransferAmount: 0,
          fundTransferCount: 0,
          payments: paymentsResponse.content
        };

        summariesResponse.forEach((daySummary: PaymentSummary) => {
          aggregatedSummary.totalAmount += daySummary.totalAmount;
          aggregatedSummary.totalCount += daySummary.totalCount;
          aggregatedSummary.cashAmount += daySummary.cashAmount;
          aggregatedSummary.cashCount += daySummary.cashCount;
          aggregatedSummary.cardAmount += daySummary.cardAmount;
          aggregatedSummary.cardCount += daySummary.cardCount;
          aggregatedSummary.fundTransferAmount += daySummary.fundTransferAmount;
          aggregatedSummary.fundTransferCount += daySummary.fundTransferCount;
        });

        console.log('Aggregated payment summary:', aggregatedSummary);
        setSummary(aggregatedSummary);
        setAllTimeData(null); // Clear all-time data when not in all-time view
      }
    } catch (e: any) {
      console.error('Error fetching payments:', e);
      setError(e.message || 'Failed to fetch payment data. Please try again.');
      setSummary(null);
      setAllTimeData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTimeData = async () => {
    try {
      // Fetch all payments to process into monthly breakdown
      const allPayments = await paymentService.getAllPayments(0, 10000); // Get large number to capture all
      
      // Group payments by member and month
      const memberMonthMap = new Map<string, MonthlyMemberPayment>();
      const monthsSet = new Set<string>();

      allPayments.content.forEach(payment => {
        const month = payment.paymentDate.slice(0, 7); // Extract YYYY-MM
        monthsSet.add(month);
        
        const key = `${payment.member.id}-${month}`;
        
        if (!memberMonthMap.has(key)) {
          memberMonthMap.set(key, {
            memberId: payment.member.id,
            memberName: payment.member.fullName,
            memberNic: payment.member.nicPassport || 'N/A',
            membershipType: payment.member.membershipType,
            peakHours: payment.member.peakHours || 'N/A',
            startDate: payment.member.startDate || 'N/A',
            memberStatus: payment.member.status || 'ACTIVE',
            month: month,
            payments: [],
            totalAmount: 0
          });
        }

        const memberMonth = memberMonthMap.get(key)!;
        memberMonth.payments.push({
          paymentId: payment.id,
          amount: payment.amount,
          paymentMethod: payment.paymentMethod,
          paymentDate: payment.paymentDate,
          transactionId: payment.transactionId,
          notes: payment.notes
        });
        memberMonth.totalAmount += payment.amount;
      });

      // Convert to sorted arrays
      const months = Array.from(monthsSet).sort();
      const memberPayments = Array.from(memberMonthMap.values())
        .sort((a, b) => a.memberName.localeCompare(b.memberName));

      setAllTimeData({
        months,
        memberPayments
      });
      setSummary(null); // Clear regular summary when in all-time view
    } catch (error) {
      console.error('Error fetching all-time data:', error);
      throw error;
    }
  };

  // Date label formatting
  const getDateLabel = () => {
    if (tab === 'daily') {
      return new Date(selectedDate).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' });
    } else if (tab === 'weekly') {
      if (weekRange.start && weekRange.end) {
        return `${new Date(weekRange.start).toLocaleDateString()} - ${new Date(weekRange.end).toLocaleDateString()}`;
      }
      return 'Select Week';
    } else {
      const [year, month] = selectedMonth.split('-');
      return `${new Date(Number(year), Number(month) - 1).toLocaleString('default', { month: 'long' })} ${year}`;
    }
  };

  // Week picker helper
  const handleWeekChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWeekRange(getReportWeekRange(e.target.value));
  };

  // Handle tab change
  const handleTabChange = (newTab: 'daily' | 'weekly' | 'monthly' | 'alltime') => {
    setTab(newTab);
    // Reset date selections based on tab
    if (newTab === 'daily') {
      setSelectedDate(getReportToday());
    } else if (newTab === 'weekly') {
      setWeekRange(getReportWeekRange());
    } else if (newTab === 'monthly') {
      setSelectedMonth(getReportMonth());
    }
    // No date selection needed for all-time view
  };

  const scrollAllTimeTable = (direction: 'left' | 'right') => {
    allTimeTableRef.current?.scrollBy({
      left: direction === 'left' ? -520 : 520,
      behavior: 'smooth'
    });
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format payment method
  const formatPaymentMethod = (method: string) => {
    switch (method) {
      case 'CASH': return 'Cash';
      case 'CARD': return 'Card';
      case 'FUND_TRANSFER': return 'Transfer';
      default: return method;
    }
  };

  // Format peak hours
  const formatPeakHours = (peakHours: string) => {
    switch (peakHours) {
      case 'PEAK': return 'Peak';
      case 'OFF_PEAK': return 'Off-Peak';
      default: return peakHours;
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString || dateString === 'N/A') return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  // Filter members based on search text and status
  const getFilteredMemberIds = (memberPayments: MonthlyMemberPayment[], filterText: string): number[] => {
    let filteredMembers = memberPayments;

    // Filter by active status first if excludeInactive is true
    if (excludeInactive) {
      filteredMembers = memberPayments.filter(mp => mp.memberStatus === 'ACTIVE');
    }

    // Then apply text filter
    if (!filterText.trim()) {
      return Array.from(new Set(filteredMembers.map(mp => mp.memberId)));
    }

    const searchTerm = filterText.toLowerCase();
    const filteredMemberIds = new Set<number>();

    filteredMembers.forEach(mp => {
      const searchableText = [
        mp.memberName,
        mp.memberNic,
        mp.membershipType,
        formatPeakHours(mp.peakHours),
        formatDate(mp.startDate),
        mp.startDate // Include raw date for searches
      ].join(' ').toLowerCase();

      if (searchableText.includes(searchTerm)) {
        filteredMemberIds.add(mp.memberId);
      }
    });

    return Array.from(filteredMemberIds);
  };

  return (
    <div className="fitness-color nunito-body payments-dashboard-page">
      {/* Header Section Start */}
      <header className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/dashboard" className="back-button">
              <i className="ri-arrow-left-s-line"></i>
            </Link>
            <span className="header-title">Payments Dashboard</span>
          </div>
          <div className="header-right">
            <Link to="/payments/analytics" className="analytics-report-link">Payment analytics</Link>
            <Link to="/payments" className="upload-button">
              <i className="ri-add-line"></i>
            </Link>
          </div>
        </div>
      </header>
      {/* Header Section End */}

      {/* Payments Section Start */}
      <section className="fitness-section">
        <div className="custom-container">
          <div className="fitness-card">
            <div className="card-body">
              {/* Tab Navigation */}
              <div className="fitness-tab-row">
                <button 
                  className={`fitness-tab ${tab === 'daily' ? 'active' : ''}`} 
                  onClick={() => handleTabChange('daily')}
                >
                  Daily
                </button>
                <button 
                  className={`fitness-tab ${tab === 'weekly' ? 'active' : ''}`} 
                  onClick={() => handleTabChange('weekly')}
                >
                  Weekly
                </button>
                <button 
                  className={`fitness-tab ${tab === 'monthly' ? 'active' : ''}`} 
                  onClick={() => handleTabChange('monthly')}
                >
                  Monthly
                </button>
                <button 
                  className={`fitness-tab ${tab === 'alltime' ? 'active' : ''}`} 
                  onClick={() => handleTabChange('alltime')}
                >
                  All Time
                </button>
              </div>

              {/* Date Selection */}
              {tab !== 'alltime' && (
                <div className="date-selection">
                  <div className="form-box">
                    <label className="form-label">Select Date</label>
                    {tab === 'daily' && (
                      <DateField value={selectedDate} onChange={setSelectedDate} />
                    )}
                    {tab === 'weekly' && (
                      <DateField value={weekRange.start} onChange={value => handleWeekChange({ target: { value } } as React.ChangeEvent<HTMLInputElement>)} />
                    )}
                    {tab === 'monthly' && (
                      <DateField value={selectedMonth + "-01"} onChange={value => setSelectedMonth(value.slice(0, 7))} />
                    )}
                  </div>
                  <div className="selected-date">
                    {getDateLabel()}
                  </div>
                </div>
              )}

              {/* Summary Cards - Hide for all-time view */}
              {tab !== 'alltime' && (
                <>
                  {/* Primary row */}
                  <div className="payment-summary-grid primary">
                    <div className="fitness-stats-box">
                      <div className="fitness-stats-icon" style={{ background: '#10B981' }}>
                        <i className="ri-money-dollar-circle-line"></i>
                      </div>
                      <div className="fitness-stats-content">
                        <h5>{summary ? formatCurrency(summary.totalAmount) : '-'}</h5>
                        <h6>Total Revenue</h6>
                      </div>
                    </div>

                    <div className="fitness-stats-box">
                      <div className="fitness-stats-icon" style={{ background: '#3B82F6' }}>
                        <i className="ri-file-list-3-line"></i>
                      </div>
                      <div className="fitness-stats-content">
                        <h5>{summary?.totalCount ?? '-'}</h5>
                        <h6>Total Payments</h6>
                      </div>
                    </div>
                  </div>

                  {/* Secondary row */}
                  <div className="payment-summary-grid secondary">
                    <div className="fitness-stats-box">
                      <div className="fitness-stats-icon" style={{ background: '#F59E0B' }}>
                        <i className="ri-hand-coin-line"></i>
                      </div>
                      <div className="fitness-stats-content">
                        <h5>{summary ? formatCurrency(summary.cashAmount) : '-'}</h5>
                        <h6>Cash ({summary?.cashCount ?? 0})</h6>
                      </div>
                    </div>

                    <div className="fitness-stats-box">
                      <div className="fitness-stats-icon" style={{ background: '#8B5CF6' }}>
                        <i className="ri-bank-card-line"></i>
                      </div>
                      <div className="fitness-stats-content">
                        <h5>{summary ? formatCurrency(summary.cardAmount) : '-'}</h5>
                        <h6>Card ({summary?.cardCount ?? 0})</h6>
                      </div>
                    </div>

                    <div className="fitness-stats-box">
                      <div className="fitness-stats-icon" style={{ background: '#EF4444' }}>
                        <i className="ri-exchange-line"></i>
                      </div>
                      <div className="fitness-stats-content">
                        <h5>{summary ? formatCurrency(summary.fundTransferAmount) : '-'}</h5>
                        <h6>Transfer ({summary?.fundTransferCount ?? 0})</h6>
                      </div>
                    </div>
                  </div>
                </>
              )}
               
              {/* All Time Report View */}
              {tab === 'alltime' ? (
                <div className="alltime-report">
                  <div className="alltime-header">
                    <h3>All Members Payment History by Month</h3>
                    <p>Total records: {allTimeData?.memberPayments?.length || 0} member-month combinations</p>
                  </div>

                  {/* Member Filter */}
                  <div className="member-filter-container">
                    <div className="form-box">
                      <label className="form-label">
                        <i className="ri-search-line"></i>
                        Filter Members
                      </label>
                      <input
                        type="text"
                        value={memberFilter}
                        onChange={(e) => setMemberFilter(e.target.value)}
                        placeholder="Search by name, NIC, membership type, peak hours..."
                        className="form-control member-filter-input"
                      />
                      {memberFilter && (
                        <button
                          type="button"
                          className="clear-filter-btn"
                          onClick={() => setMemberFilter('')}
                          title="Clear filter"
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      )}
                    </div>
                    
                    {/* Exclude Inactive Members Checkbox */}
                    <div className="form-box checkbox-container">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={excludeInactive}
                          onChange={(e) => setExcludeInactive(e.target.checked)}
                          className="checkbox-input"
                        />
                        <span className="checkbox-text">
                          <i className="ri-user-unfollow-line"></i>
                          Exclude Inactive Members
                        </span>
                      </label>
                    </div>

                    {memberFilter && allTimeData && (
                      <div className="filter-results-info">
                        Showing {getFilteredMemberIds(allTimeData.memberPayments, memberFilter).length} of {Array.from(new Set(allTimeData.memberPayments.map(mp => mp.memberId))).length} members
                      </div>
                    )}
                  </div>
                  
                  {loading && (
                    <div className="loading-state">
                      <div className="spinner"></div>
                      Loading all-time payment data...
                    </div>
                  )}
                  
                  {error && (
                    <div className="error-state">
                      <div className="text-danger">{error}</div>
                    </div>
                  )}
                  
                  {!loading && !error && allTimeData && (
                    <>
                      <div className="alltime-scroll-controls" aria-label="Payment history table navigation">
                        <span>Scroll through months</span>
                        <div className="alltime-scroll-buttons">
                          <button
                            type="button"
                            onClick={() => scrollAllTimeTable('left')}
                            aria-label="Scroll payment history left"
                          >
                            <i className="ri-arrow-left-line" aria-hidden="true"></i>
                          </button>
                          <button
                            type="button"
                            onClick={() => scrollAllTimeTable('right')}
                            aria-label="Scroll payment history right"
                          >
                            <i className="ri-arrow-right-line" aria-hidden="true"></i>
                          </button>
                        </div>
                      </div>
                      <div ref={allTimeTableRef} className="alltime-table-container" tabIndex={0}>
                        <table
                        className="alltime-table"
                        style={{ minWidth: `${220 + (allTimeData.months.length * 132)}px` }}
                      >
                        <thead>
                          <tr>
                            <th className="member-header">Member</th>
                            {allTimeData.months.map(month => (
                              <th key={month} className="month-header">
                                {new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {/* Group by member - Apply filter */}
                          {getFilteredMemberIds(allTimeData.memberPayments, memberFilter)
                            .map(memberId => {
                              const memberData = allTimeData.memberPayments.filter(mp => mp.memberId === memberId);
                              const firstEntry = memberData[0];
                              if (!firstEntry) return null;
                              
                              return (
                                <tr key={memberId} className="member-row">
                                  <td className="member-cell">
                                    <div className="member-details">
                                      <div className="member-name">{firstEntry.memberName}</div>
                                      <div className="member-nic">NIC: {firstEntry.memberNic}</div>
                                      <div className="member-type">{firstEntry.membershipType}</div>
                                      <div className="member-peak-hours">{formatPeakHours(firstEntry.peakHours)}</div>
                                      <div className="member-start-date">Started: {formatDate(firstEntry.startDate)}</div>
                                    </div>
                                  </td>
                                  {allTimeData.months.map(month => {
                                    const monthData = memberData.find(mp => mp.month === month);
                                    return (
                                      <td key={`${memberId}-${month}`} className="payment-cell">
                                        {monthData ? (
                                          <div className="payment-info">
                                            {monthData.payments.length === 1 ? (
                                              <div className="payment-amount-row">
                                                <div className="total-amount">
                                                  {formatCurrency(monthData.totalAmount)}
                                                </div>
                                                <Link
                                                  to={`/payments/edit/${monthData.payments[0].paymentId}`}
                                                  className="payment-edit-link"
                                                  title="Edit payment"
                                                  aria-label="Edit payment"
                                                >
                                                  <i className="ri-edit-line" aria-hidden="true"></i>
                                                </Link>
                                              </div>
                                            ) : (
                                              <div className="total-amount">
                                                {formatCurrency(monthData.totalAmount)}
                                              </div>
                                            )}
                                            {monthData.payments.length > 1 && (
                                              <div className="payment-breakdown">
                                                {monthData.payments.map((payment, idx) => (
                                                  <div key={payment.paymentId} className="payment-item">
                                                    <div className="payment-amount-row">
                                                      <span className="payment-amount">
                                                        {formatCurrency(payment.amount)}
                                                      </span>
                                                      <Link
                                                        to={`/payments/edit/${payment.paymentId}`}
                                                        className="payment-edit-link"
                                                        title="Edit payment"
                                                        aria-label="Edit payment"
                                                      >
                                                        <i className="ri-edit-line" aria-hidden="true"></i>
                                                      </Link>
                                                    </div>
                                                    <span className="payment-method-mini">
                                                      {formatPaymentMethod(payment.paymentMethod)} - {new Date(payment.paymentDate).toLocaleDateString('en-US', { 
                                                        month: 'short', 
                                                        day: 'numeric' 
                                                      })}
                                                    </span>
                                                    {payment.notes && (
                                                      <div className="payment-notes-breakdown">
                                                        {payment.notes}
                                                      </div>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                            {monthData.payments.length === 1 && (
                                              <div className="single-payment-details">
                                                <span className="payment-method-small">
                                                  {formatPaymentMethod(monthData.payments[0].paymentMethod)} - {new Date(monthData.payments[0].paymentDate).toLocaleDateString('en-US', { 
                                                    month: 'short', 
                                                    day: 'numeric' 
                                                  })}
                                                </span>
                                                {monthData.payments[0].notes && (
                                                  <div className="payment-notes-breakdown">
                                                    {monthData.payments[0].notes}
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          <div className="no-payment">-</div>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                        </tbody>
                        </table>
                      </div>
                    </>
                  )}
                  
                  {!loading && !error && (!allTimeData || allTimeData.memberPayments.length === 0) && (
                    <div className="empty-state">
                      <p>No payment data available</p>
                    </div>
                  )}
                </div>
              ) : (
                /* Regular Payments List for other tabs */
                <div className="payments-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Transaction ID</th>
                        <th>Member</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Date</th>
                        <th>Notes</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && (
                        <tr className="loading">
                          <td colSpan={7}>
                            <div className="spinner"></div>
                            Loading...
                          </td>
                        </tr>
                      )}
                      {error && (
                        <tr className="error">
                          <td colSpan={7}>
                            <div className="text-danger">{error}</div>
                          </td>
                        </tr>
                      )}
                      {!loading && !error && summary?.payments?.length === 0 && (
                        <tr className="empty">
                          <td colSpan={7}>
                            No payments found for this period
                          </td>
                        </tr>
                      )}
                      {!loading && !error && summary?.payments?.map((payment) => (
                        <tr key={payment.id}>
                          <td>
                            <span className="transaction-id">{payment.transactionId}</span>
                          </td>
                          <td>
                            <div className="member-info">
                              <div className="member-name">{payment.member.fullName}</div>
                              <div className="member-nic">{payment.member.nicPassport || 'N/A'}</div>
                              <div className="member-type">{payment.member.membershipType}</div>
                              <div className="member-peak-hours">{formatPeakHours(payment.member.peakHours)}</div>
                            </div>
                          </td>
                          <td>
                            <span className="amount">{formatCurrency(payment.amount)}</span>
                          </td>
                          <td>
                            <span className={`payment-method ${payment.paymentMethod.toLowerCase()}`}>
                              {formatPaymentMethod(payment.paymentMethod)}
                            </span>
                          </td>
                          <td>
                            <span className="payment-date">
                              {new Date(payment.paymentDate).toLocaleDateString()}
                            </span>
                          </td>
                          <td>
                            <span className="payment-notes">
                              {payment.notes || '-'}
                            </span>
                          </td>
                          <td>
                            <Link
                              to={`/payments/edit/${payment.id}`}
                              className="payment-edit-link"
                              title="Edit payment"
                              aria-label="Edit payment"
                            >
                              <i className="ri-edit-line" aria-hidden="true"></i>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* Payments Section End */}
    </div>
  );
};

export default PaymentsDashboard;
